/**
 * Word-level diff for the copy editor's "what changed" view.
 *
 * There is no live preview of a draft (plan Q3), so the diff *is* how an editor
 * judges an edit before publishing. It has to work in Arabic as well as
 * English, which rules out character diffs (they shred a word's shaping) and
 * rules out splitting on ASCII whitespace alone.
 */

export type DiffOp = 'same' | 'add' | 'remove';

export interface DiffSegment {
  op: DiffOp;
  text: string;
}

/** Split into words *and* the whitespace between them, so a rejoin is lossless. */
function tokenize(input: string): string[] {
  return input.length === 0 ? [] : (input.match(/\s+|[^\s]+/gu) ?? []);
}

/**
 * Longest-common-subsequence table over tokens. Copy strings are short (the
 * longest in the catalog is 212 characters), so the quadratic table is a
 * handful of cells; we still cap it so a pathological value can't lock the tab.
 */
const MAX_TOKENS = 400;

export function diffWords(before: string, after: string): DiffSegment[] {
  const a = tokenize(before);
  const b = tokenize(after);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    // Fall back to a whole-value replacement rather than an unbounded table.
    const out: DiffSegment[] = [];
    if (before) out.push({ op: 'remove', text: before });
    if (after) out.push({ op: 'add', text: after });
    return out;
  }

  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      const row = lcs[i]!;
      const nextRow = lcs[i + 1]!;
      row[j] = a[i] === b[j] ? nextRow[j + 1]! + 1 : Math.max(nextRow[j]!, row[j + 1]!);
    }
  }

  const raw: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      raw.push({ op: 'same', text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      raw.push({ op: 'remove', text: a[i]! });
      i++;
    } else {
      raw.push({ op: 'add', text: b[j]! });
      j++;
    }
  }
  while (i < a.length) raw.push({ op: 'remove', text: a[i++]! });
  while (j < b.length) raw.push({ op: 'add', text: b[j++]! });

  // Merge adjacent segments of the same op so the DOM stays small and
  // screen readers announce a phrase rather than word-by-word fragments.
  const merged: DiffSegment[] = [];
  for (const seg of raw) {
    const last = merged[merged.length - 1];
    if (last?.op === seg.op) last.text += seg.text;
    else merged.push({ ...seg });
  }
  return merged;
}

export function hasChanges(segments: readonly DiffSegment[]): boolean {
  return segments.some((s) => s.op !== 'same');
}
